import { apiFetch, ApiError, dataApi } from './api';
import {
  cacheSet,
  db,
  pendingMutations,
  type OutboxMutation,
} from './db';

export const SYNC_EVENT = 'heritage:sync';

export interface FlushResult {
  sent: number;
  failed: number;
  remaining: number;
}

export async function flushOutbox(): Promise<FlushResult> {
  const database = db();
  if (!database) return { sent: 0, failed: 0, remaining: 0 };
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { sent: 0, failed: 0, remaining: await pendingMutations() };
  }

  const items = await database.outbox.orderBy('createdAt').toArray();
  let sent = 0;
  let failed = 0;

  for (const item of items) {
    const outcome = await sendOutboxItem(item, database);
    if (outcome === 'sent') sent += 1;
    else if (outcome === 'failed') failed += 1;
    else break;
  }

  const remaining = await pendingMutations();
  window.dispatchEvent(
    new CustomEvent(SYNC_EVENT, { detail: { sent, failed, remaining } }),
  );
  return { sent, failed, remaining };
}

async function sendOutboxItem(
  item: OutboxMutation,
  database: NonNullable<ReturnType<typeof db>>,
): Promise<'sent' | 'failed' | 'stop'> {
  try {
    await apiFetch(item.path, {
      method: item.method,
      body: item.body ?? undefined,
      skipQueue: true,
    });
    await database.outbox.delete(item.id!);
    return 'sent';
  } catch (error) {
    if (error instanceof ApiError && error.status === 0) {
      return 'stop';
    }

    if (
      error instanceof ApiError &&
      (error.status >= 500 || error.status === 401 || error.status === 403)
    ) {
      await database.outbox.update(item.id!, {
        attempts: item.attempts + 1,
        lastError: error.message,
      });
      return 'stop';
    }

    if (error instanceof ApiError && error.status === 409) {
      await database.conflicts.add({
        familyId: item.familyId,
        entityType: item.path,
        local: item.body ?? '',
        server: JSON.stringify(error.details ?? {}),
        createdAt: Date.now(),
      });
    }

    await database.outbox.delete(item.id!);
    return 'failed';
  }
}

export async function warmCache(familyId: string): Promise<void> {
  const [tree, chats, notifications, mineFacts, allFacts] =
    await Promise.allSettled([
      dataApi.tree(familyId),
      dataApi.chats(familyId),
      dataApi.notifications(),
      dataApi.familyFacts(familyId, 50, 'mine'),
      dataApi.familyFacts(familyId, 50, 'all'),
    ]);

  if (tree.status === 'fulfilled') {
    await cacheSet(`tree:${familyId}`, tree.value, familyId);
  }
  if (chats.status === 'fulfilled') {
    await cacheSet(`chats:${familyId}`, chats.value, familyId);
  }
  if (notifications.status === 'fulfilled') {
    await cacheSet('notifications', notifications.value, null);
  }
  if (mineFacts.status === 'fulfilled') {
    await cacheSet(`facts:${familyId}:mine`, mineFacts.value, familyId);
  }
  if (allFacts.status === 'fulfilled') {
    await cacheSet(`facts:${familyId}:all`, allFacts.value, familyId);
  }
}

export function startSyncLoop(getFamilyId: () => string | null): () => void {
  let stopped = false;

  const run = async () => {
    if (stopped) return;
    await flushOutbox();
    const familyId = getFamilyId();
    if (familyId && navigator.onLine) {
      await warmCache(familyId).catch(() => undefined);
    }
  };

  const onOnline = () => void run();
  const onVisibility = () => {
    if (document.visibilityState === 'visible') void run();
  };

  void run();
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibility);
  const interval = window.setInterval(() => {
    if (navigator.onLine) void run();
  }, 60_000);

  return () => {
    stopped = true;
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibility);
    window.clearInterval(interval);
  };
}
